import i18next from "i18next";
import { initReactI18next } from "react-i18next";
import th from "../locales/th.json";
import en from "../locales/en.json";

export type Lang = "th" | "en";
export const LANGS: Lang[] = ["th", "en"];
const STORAGE_KEY = "lang";

export function isLang(v: unknown): v is Lang {
  return v === "th" || v === "en";
}

export function storedLang(): Lang {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (isLang(v)) return v;
  } catch {
    /* private mode */
  }
  return "th";
}

export function currentLang(): Lang {
  return isLang(i18next.language) ? i18next.language : "th";
}

export async function setLang(lang: Lang): Promise<void> {
  try {
    localStorage.setItem(STORAGE_KEY, lang);
  } catch {
    /* ignore */
  }
  await i18next.changeLanguage(lang);
}

export function initI18n(lang: Lang = storedLang()): typeof i18next {
  if (i18next.isInitialized) return i18next;
  void i18next.use(initReactI18next).init({
    resources: { th: { translation: th }, en: { translation: en } },
    lng: lang,
    fallbackLng: "th",
    interpolation: { escapeValue: false },
    returnNull: false,
  });
  i18next.on("languageChanged", (l) => {
    if (typeof document !== "undefined") document.documentElement.lang = l;
  });
  if (typeof document !== "undefined") document.documentElement.lang = lang;
  return i18next;
}

/** เลือกข้อความจาก I18nText ({th, en}) ตามภาษา UI */
export function pickText(text: Record<string, string> | null | undefined, lang: Lang = currentLang()): string {
  if (!text) return "";
  return text[lang] ?? text.th ?? text.en ?? Object.values(text)[0] ?? "";
}

/**
 * [T-090 · #255] ลิงก์/redirect ไป `/auth/login` ของ worker — แนบ `lang` = ภาษาปัจจุบันของ UI เสมอ
 * (oidc-integration §3.1: แอปที่รู้ภาษาที่ผู้ใช้เลือกควรส่ง `lang` ทุกครั้ง — server อ่าน localStorage ไม่ได้
 * จึงพามาบน URL · worker sanitize ให้อยู่ใน 7 ภาษาของ UWU ID เอง) · `next`/`reauth` ของ flow เดิมคงครบ
 */
export function authLoginHref(params: { next?: string; reauth?: boolean } = {}): string {
  const sp = new URLSearchParams();
  if (params.next) sp.set("next", params.next);
  if (params.reauth) sp.set("reauth", "1");
  sp.set("lang", currentLang());
  return "/auth/login?" + sp.toString();
}
